import React, { useEffect, useRef, useState } from "react";
import {
  CellProps,
  Column,
  FilterTypes,
  Hooks,
  IdType,
  Row,
  SortingRule,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { useDebouncedCallback } from "use-debounce";

import { TableRenderer } from "@/components/elements/TableRenderer";
import { Checkbox } from "@/components/primitives/Checkbox";

import { TableFilters } from "./TableFilters";
import { TablePagination } from "./TablePagination";

const DEFAULT_PAGE_SIZE = 15;

export type TableSelection<T> = {
  selectedRows: T[];
  /** Selected rows on other pages than the one shown. */
  hiddenCount: number;
  clearSelection: () => void;
};

export type TableSelectionOptions<T> = {
  /** Keys selection by id rather than array index, so a refetch can't move it onto another row. */
  getRowId: (row: T) => string;
  actions: (selection: TableSelection<T>) => React.ReactNode;
};

const SELECTION_COLUMN_ID = "selection";

function stopPropagation(e: React.MouseEvent) {
  e.stopPropagation();
}

function addSelectionColumn<T extends object>(hooks: Hooks<T>) {
  hooks.visibleColumns.push((columns) => [
    {
      id: SELECTION_COLUMN_ID,
      disableSortBy: true,
      width: 40,
      Header: ({ getToggleAllPageRowsSelectedProps }) => (
        <div onClick={stopPropagation}>
          <Checkbox
            {...getToggleAllPageRowsSelectedProps({ title: undefined })}
            aria-label="Select all rows on this page"
          />
        </div>
      ),
      Cell: ({ row }: CellProps<T>) => (
        // Negative margin cancels the cell padding so a near miss still hits the label.
        <label
          className="-m-2 flex cursor-pointer p-2"
          onClick={stopPropagation}
        >
          <Checkbox
            {...row.getToggleRowSelectedProps({ title: undefined })}
            aria-label="Select row"
          />
        </label>
      ),
    },
    ...columns,
  ]);
}

function noSelectionColumn() {}

export function FilteredDataTable<T extends object>(props: {
  data: T[];
  columns: Column<T>[];
  filterTypes?: FilterTypes<T>;
  hiddenColumns?: string[];
  initialSortBy?: SortingRule<T>[];
  globalFilter?:
    | ((rows: Row<T>[], columnIds: IdType<T>[], filterValue: any) => Row<T>[])
    | string;
  onRowClick: (
    row: T,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => void;
  actions?: React.ReactNode;
  searchPlaceholder?: string;
  /** Enables row selection; `actions` is rendered in place of the table's actions while rows are selected. */
  selection?: TableSelectionOptions<T>;
}) {
  const [sorting, setSorting] = useState<SortingRule<T>[]>(
    props.initialSortBy ?? [{ id: "createdAt", desc: true }],
  );

  const [pageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [localSearch, setLocalSearch] = useState<string>("");

  const {
    canNextPage,
    canPreviousPage,
    gotoPage,
    getTableBodyProps,
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageCount,
    setGlobalFilter: setTableGlobalFilter,
    state: { sortBy, filters, globalFilter, pageIndex },
    allColumns,
    selectedFlatRows,
    toggleAllRowsSelected,
  } = useTable<T>(
    {
      columns: props.columns,
      data: props.data,
      filterTypes: props.filterTypes,
      initialState: {
        sortBy: sorting,
        hiddenColumns: props.hiddenColumns ?? [],
        pageSize,
      },
      globalFilter: props.globalFilter,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      autoResetPage: false,
      autoResetSelectedRows: false,
      getRowId: props.selection?.getRowId,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    props.selection ? addSelectionColumn : noSelectionColumn,
  );

  useEffect(() => {
    setSorting(sortBy);
  }, [sortBy]);

  const pageIndexRef = useRef(pageIndex);
  pageIndexRef.current = pageIndex;

  useEffect(() => {
    if (pageIndexRef.current !== 0) {
      gotoPage(0);
    }
  }, [filters, gotoPage]);

  useEffect(() => {
    const lastPageIndex = Math.max(pageCount - 1, 0);
    if (pageIndex > lastPageIndex) {
      gotoPage(lastPageIndex);
    }
  }, [pageCount, pageIndex, gotoPage]);

  // Never act on rows the user can no longer see because a filter now hides them.
  useEffect(() => {
    toggleAllRowsSelected(false);
  }, [filters, globalFilter, toggleAllRowsSelected]);

  const visibleRowIds = new Set(page.map((row) => row.id));
  const selection: TableSelection<T> = {
    selectedRows: selectedFlatRows.map((row) => row.original),
    hiddenCount: selectedFlatRows.filter((row) => !visibleRowIds.has(row.id))
      .length,
    clearSelection: () => toggleAllRowsSelected(false),
  };
  const isSelecting =
    props.selection !== undefined && selectedFlatRows.length > 0;

  function onRowClick(
    original: T,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) {
    const opensInNewTab = event.metaKey || event.ctrlKey;
    const row = page.find((r) => r.original === original);
    if (isSelecting && !opensInNewTab && row) {
      row.toggleRowSelected();
      return;
    }
    props.onRowClick(original, event);
  }

  const actions =
    isSelecting && props.selection
      ? props.selection.actions(selection)
      : props.actions;

  const debouncedSetGlobal = useDebouncedCallback((val: string) => {
    setTableGlobalFilter(val || undefined);
  }, 300);

  function onSearchChange(searchTerm: string) {
    setLocalSearch(searchTerm);
    toggleAllRowsSelected(false);
    debouncedSetGlobal(searchTerm);
    if (pageIndex !== 0) {
      gotoPage(0);
    }
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex flex-col-reverse gap-4 xl:flex-row">
          <TableFilters
            columns={allColumns}
            searchTerm={localSearch}
            onSearchChange={onSearchChange}
            searchPlaceholder={props.searchPlaceholder}
            onResetFilters={() => {
              onSearchChange("");
              allColumns.forEach((column) => {
                if (column.canFilter && column.setFilter) {
                  column.setFilter(undefined);
                }
              });
            }}
            isFiltered={filters.length > 0 || localSearch !== ""}
          />
          {actions && <div className="ml-auto self-end">{actions}</div>}
        </div>
      </div>
      <TableRenderer
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        rows={page}
        prepareRow={prepareRow}
        columns={props.columns}
        onRowClick={onRowClick}
        isSortable
      />
      <TablePagination
        className="mt-3"
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={pageSize}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        onChangePage={gotoPage}
        totalRows={rows.length}
      />
    </div>
  );
}
